import { useMemo } from 'react'
import dayjs from 'dayjs'

import type { OldestInvoiceData } from '../types/supplier'
import type { Match } from '@/api/useSupplierReconciliation'

function useOldestInvoices(matches: Match[]): OldestInvoiceData[] {
  return useMemo(() => {
    if (!matches?.length) return []

    const pastDueInvoices: OldestInvoiceData[] = []
    const now = dayjs()

    matches.forEach((match) => {
      // Skip voided matches entirely
      if (match.status === 'void') return

      // Try top-level fields first (aggregated data)
      if (match.invoice_bol_date) {
        const dueDate = dayjs(match.invoice_due_date)

        // Create entry for each invoice number in the match
        match.invoice_numbers.forEach((invoiceNumberObj) => {
          const amount = (match.invoice_total ?? 0) / (match.invoice_numbers?.length || 1)
          const daysOverdue = now.diff(dueDate, 'day')

          pastDueInvoices.push({
            invoiceNumber: invoiceNumberObj.number || 'Unknown',
            supplierName: match.supplier_name || 'Unknown',
            amountDue: amount,
            daysUntilDue: -daysOverdue, // Negative for past due
            status: match.status,
            supplierId: match.supplier_id,
            matchId: match.id,
            invoiceBolDate: match.invoice_bol_date ?? '',
          })
        })
      }
      // Fallback to details if top-level is empty
      else if (match.details?.length) {
        match.details.forEach((detail) => {
          if (match.invoice_bol_date) {
            const dueDate = dayjs(detail.invoice_due_date)
            const daysOverdue = now.diff(dueDate, 'day')

            pastDueInvoices.push({
              invoiceNumber: detail.invoice_number || 'Unknown',
              supplierName: match.supplier_name || 'Unknown',
              amountDue: detail.invoice_total ?? 0,
              daysUntilDue: -daysOverdue, // Negative for past due
              status: match.status,
              supplierId: match.supplier_id,
              matchId: match.id,
              invoiceBolDate: match.invoice_bol_date,
            })
          }
        })
      }
    })

    // DEDUPLICATE by invoice number (keep the one with highest amount)
    const uniqueInvoices = new Map<string, OldestInvoiceData>()

    pastDueInvoices.forEach((invoice) => {
      const key = `${invoice.invoiceNumber}-${invoice.supplierId}`
      const existing = uniqueInvoices.get(key)

      if (!existing || invoice.amountDue > existing.amountDue) {
        uniqueInvoices.set(key, invoice)
      }
    })

    const deduplicatedInvoices = Array.from(uniqueInvoices.values())

    // Sort by days until due (ascending - most negative = most overdue)
    return deduplicatedInvoices
      .sort((a, b) => dayjs(a.invoiceBolDate).valueOf() - dayjs(b.invoiceBolDate).valueOf())
      .slice(0, 10)
  }, [matches])
}

export { useOldestInvoices }
